import React, { useState } from 'react';
import { apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import EmployeeSelect from '../../../components/pickers/EmployeeSelect';

/**
 * Visitor registration drawer. mode: 'pre' (pre-register) or 'walkin'
 * (register + immediate check-in). Reused by VisitorLog and the mobile tile.
 */
export default function VisitorRegister({ mode = 'pre', onClose, onSaved, embedded = false }) {
  const [f, setF] = useState({ visitor_name: '', company: '', phone: '', purpose: '', location: 'office', expected_at: '', expected_date: new Date().toISOString().slice(0, 10), vehicle_number: '', id_proof_type: '', id_proof_number: '' });
  const [meet, setMeet] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.visitor_name.trim()) { toast.error('Visitor name is required'); return; }
    setBusy(true);
    try {
      const payload = { ...f, host_employee_id: meet?.id || null, host_name: meet?.name || f.host_name || null };
      const url = mode === 'walkin' ? '/admin/visitors/walk-in' : '/admin/visitors/pre-register';
      await apiPost(url, payload);
      toast.success(mode === 'walkin' ? 'Visitor checked in' : 'Visitor pre-registered');
      onSaved && onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const Form = (
    <form onSubmit={submit} className="space-y-3" data-testid="visitor-register-form">
      <label className="block"><span className="lbl">Visitor Name *</span><input className="inp" value={f.visitor_name} onChange={(e) => setF({ ...f, visitor_name: e.target.value })} data-testid="visitor-name" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="lbl">Company</span><input className="inp" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></label>
        <label className="block"><span className="lbl">Phone</span><input className="inp" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
      </div>
      <label className="block"><span className="lbl">Purpose</span><input className="inp" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="visitor-purpose" /></label>
      <div><span className="lbl">Person to Meet</span><EmployeeSelect value={meet} onSelect={setMeet} testid="visitor-meet" /></div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="lbl">Location</span><input className="inp" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="office / site" /></label>
        {mode === 'pre' && <label className="block"><span className="lbl">Expected At</span><input type="datetime-local" className="inp" value={f.expected_at} onChange={(e) => setF({ ...f, expected_at: e.target.value })} /></label>}
        {mode === 'walkin' && <label className="block"><span className="lbl">Vehicle No.</span><input className="inp" value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value })} /></label>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="lbl">ID Proof Type</span>
          <select className="inp" value={f.id_proof_type} onChange={(e) => setF({ ...f, id_proof_type: e.target.value })}>
            <option value="">—</option>{['Aadhaar', 'PAN', 'Driving License', 'Voter ID', 'Passport', 'Company ID'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="block"><span className="lbl">ID Number <span className="text-[9px] text-slate-400">(only last 4 stored)</span></span><input className="inp" value={f.id_proof_number} onChange={(e) => setF({ ...f, id_proof_number: e.target.value })} data-testid="visitor-idproof" /></label>
      </div>
      <button type="submit" disabled={busy} data-testid="visitor-submit" className="w-full py-2.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">
        {busy ? 'Saving…' : mode === 'walkin' ? 'Register & Check In' : 'Pre-register Visitor'}
      </button>
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
    </form>
  );

  if (embedded) return Form;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-testid="visitor-register-drawer">
        <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
          <h2 className="font-display font-bold tracking-wide uppercase text-sm">{mode === 'walkin' ? 'Walk-in Visitor' : 'Pre-register Visitor'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">{Form}</div>
      </div>
    </>
  );
}
